import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';

import { Loader } from 'ui';
import { ESGImpactIcon } from 'modules/ESG';

import { theme } from 'theme';
import { getHexColorByImpact } from 'shared/functions';
import { useReportPage } from 'shared/hooks';
import { ESGImpactTypes } from 'shared/constants/ESGImpactTypes';
import geoUrl from 'assets/world-110m.json';

import { ImpactTypeControls } from './ImpactTypeControls/ImpactTypeControls';
import { Legend } from '../ESGImpactTable/Legend/Legend';

const useStyles = makeStyles({
  root: {
    position: 'relative',
  },
  divider: {
    alignSelf: 'stretch',
    height: useReportPage ? 245 : 'auto',
  },
  svgMap: {
    marginTop: useReportPage ? -40 : 0,
    marginBottom: useReportPage ? -50 : -70,
  },
});

const ESGImpactMap = ({ data, loading, withLegend }) => {
  const classes = useStyles();
  const [impactType, setImpactType] = useState(
    withLegend ? ESGImpactTypes.TOTAL : ESGImpactTypes.ENVIRONMENT,
  );
  const [hoveredContinent, setHoveredContinent] = useState(null);

  const continentsMarkers = [
    {
      name: 'Africa',
      lon: 22,
      lat: 7,
      continents: ['Africa'],
    },
    {
      name: 'Asia & Pacific',
      lon: 82,
      lat: 55,
      continents: ['Asia', 'Oceania'],
    },
    {
      name: 'Europe',
      lon: 12,
      lat: 52,
      continents: ['Europe'],
    },
    {
      name: 'Central & South America',
      lon: -65,
      lat: -2,
      continents: ['South America'],
    },
    {
      name: 'North America',
      lon: -115,
      lat: 50,
      continents: ['North America'],
    },
  ];

  const markers = [...continentsMarkers].map(marker => {
    const { name } = marker;
    const dataItem = data.find(item => item.continent === name);

    if (!dataItem) return null;

    const { impactWeight, impactNumber } = dataItem[impactType];

    return {
      ...marker,
      impactWeight,
      impactNumber,
    };
  });

  const getFillForContinent = continent => {
    let continentToColor = continent === 'Oceania' ? 'Asia' : continent;

    if (continent === 'Asia') continentToColor = 'Asia & Pacific';
    if (continent === 'Oceania') continentToColor = 'Asia & Pacific';
    if (continent === 'South America') continentToColor = 'Central & South America';

    const continentData = data.find(
      ({ continent: continentName }) => continentName === continentToColor,
    );

    return continentData
      ? getHexColorByImpact(
          continentData[impactType].impactNumber,
          continentData[impactType].impactWeight,
        )
      : theme.palette.grey[400];
  };

  const getStyles = geo => ({
    fill: getFillForContinent(geo.properties.CONTINENT),
    stroke: theme.palette.custom.white,
    strokeWidth: 0.75,
    outline: 'none',
  });

  const handleImpactTypeChange = value => setImpactType(value);

  const handleMove = value => {
    const findName = continentsMarkers.find(({ continents }) => continents.includes(value)).name;
    setHoveredContinent(findName);
  };
  const handleLeave = () => setHoveredContinent(null);

  return (
    <div className={classes.root}>
      {loading && <Loader cover />}
      <Grid container>
        <Grid item xs>
          <Box px={2}>
            <ComposableMap className={classes.svgMap}>
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map(geo => {
                    const current = geographies.find(({ rsmKey }) => rsmKey === geo.rsmKey);
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseMove={() => handleMove(current.properties.CONTINENT)}
                        onMouseLeave={handleLeave}
                        style={{
                          default: {
                            ...getStyles(geo),
                          },
                          hover: {
                            ...getStyles(geo),
                          },
                          pressed: {
                            ...getStyles(geo),
                          },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {markers.map(marker => {
                if (!marker) return null;
                const { lon, lat, impactWeight, impactNumber, name } = marker;

                return (
                  <Marker key={name} coordinates={[lon, lat]}>
                    <ESGImpactIcon
                      name={name}
                      impactWeight={impactWeight}
                      impactNumber={impactNumber}
                      hoveredContinent={hoveredContinent}
                      tooltip
                    />
                  </Marker>
                );
              })}
            </ComposableMap>
          </Box>
        </Grid>
        <Divider className={classes.divider} orientation="vertical" />
        <Grid item xs={4}>
          <Box p={2} pl={withLegend ? 1 : 2}>
            {withLegend ? (
              <Legend column />
            ) : (
              <ImpactTypeControls onChange={handleImpactTypeChange} />
            )}
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

ESGImpactMap.defaultProps = {
  loading: false,
  withLegend: false,
  data: [],
};

ESGImpactMap.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      continent: PropTypes.oneOf([
        'Africa',
        'Asia & Pacific',
        'Europe',
        'Central & South America',
        'North America',
      ]).isRequired,
      environment: PropTypes.shape({
        impactNumber: PropTypes.number.isRequired,
        impactWeight: PropTypes.oneOf(['low', 'average', 'high']),
      }),
      social: PropTypes.shape({
        impactNumber: PropTypes.number.isRequired,
        impactWeight: PropTypes.oneOf(['low', 'average', 'high']),
      }),
      governance: PropTypes.shape({
        impactNumber: PropTypes.number.isRequired,
        impactWeight: PropTypes.oneOf(['low', 'average', 'high']),
      }),
      total: PropTypes.shape({
        impactNumber: PropTypes.number.isRequired,
        impactWeight: PropTypes.oneOf(['low', 'average', 'high']),
      }),
    }),
  ),
  loading: PropTypes.bool,
  withLegend: PropTypes.bool,
};

export default ESGImpactMap;
